import { useEffect, useMemo, useState } from "react";
import { fetchQuery, postsQuery, type Post, type PostCategory } from "@/lib/sanity";
import { ArticleList } from "@/components/ui/article/articleList";
import { ContactCard } from "@/components/ui/contact/contactCard";

type ArticlesPageProps = {
  onNavigate?: (path: string) => void;
};

/** Dedupe categories by slug, keep first occurrence (has title). */
function uniqueCategories(posts: Post[]): PostCategory[] {
  const seen = new Set<string>();
  const out: PostCategory[] = [];
  for (const post of posts) {
    for (const cat of post.categories ?? []) {
      const slug = cat?.slug?.current;
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        out.push(cat);
      }
    }
  }
  return out;
}

export function ArticlesPage({ onNavigate }: ArticlesPageProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const categories = useMemo(() => uniqueCategories(posts), [posts]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchQuery<Post[]>(postsQuery)
      .then((data) => {
        if (!cancelled) setPosts(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load articles");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const go = (path: string) => {
    if (onNavigate) onNavigate(path);
    else if (typeof window !== "undefined") {
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  return (
    <>
      <section className="border-b border-border/60 px-6 py-16 md:px-12">
        <div className="mx-auto max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Articles
          </p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl">
            記事一覧
          </h1>
          <p className="mt-6 max-w-xl text-muted-foreground leading-relaxed">
            モノバンドル社が執筆する専門性の高い記事一覧です。
          </p>
        </div>
      </section>

      {!loading && !error && categories.length > 0 && (
        <section className="border-b border-border/60 px-6 py-8 md:px-12">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-4 text-sm font-medium text-muted-foreground">カテゴリ</h2>
            <ul className="flex flex-wrap gap-4" role="list">
              {categories.map((cat) => {
                const slug = cat.slug?.current ?? "";
                if (!slug) return null;
                return (
                  <li key={slug}>
                    <button
                      type="button"
                      onClick={() => go(`/articles/${slug}`)}
                      className="cursor-pointer text-sm text-foreground underline-offset-4 transition-colors hover:underline"
                    >
                      {cat.title ?? slug}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-6xl">
          {loading && (
            <p className="py-16 text-center text-muted-foreground">読み込み中…</p>
          )}
          {error && (
            <p className="py-16 text-center text-destructive">{error}</p>
          )}
          {!loading && !error && (
            <ArticleList posts={posts} onNavigate={go} />
          )}
        </div>
      </section>

      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-4xl">
          <ContactCard />
        </div>
      </section>
    </>
  );
}
